import { useLazyQuery, useMutation } from '@apollo/client';
import { useLingui } from '@lingui/react';
import { Form, Input, InputNumber, Select, DatePicker, Divider } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs'; // dayjs,
import { useState, useEffect } from 'react';
import {
  GQL_UPDATE_DEVIS_MAINTENANCE,
  StoredDevisMaintenance,
} from '~/gql/devis/devis';
import { StoredPoste } from '~/gql/poste/poste';
import { GQL_USERS, StoredUsers } from '~/gql/users/users';
import { authClient } from '~/helpers/apollo';
import { getUsersListWithoutUsersWithTime } from '~/pages/components/common/utils-table/utils';

import DurationItem from '~/pages/components/ui/DurationItem';
import ModalCustom from '~/pages/components/ui/ModalCustom';

// copied from rc-picker/lib/interface.d.ts
declare type EventValue<DateType> = DateType | null;
declare type RangeValue<DateType> =
  | [EventValue<DateType>, EventValue<DateType>]
  | null;

type IProps = {
  maintenance: StoredDevisMaintenance;
  postes: StoredPoste[];
  title: string;
  isOpen: boolean;
  confirm: () => void;
  close: () => void;
};

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

function EditMaintenance({
  title,
  isOpen,
  confirm,
  close,
  postes,
  maintenance,
}: IProps) {
  const { i18n } = useLingui();
  const { RangePicker } = DatePicker;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [userList, setUserList] = useState<StoredUsers[]>([]);
  const [usersWithTimes, setUsersWithTimes] = useState<number[]>([]);
  const [taskName, setTaskName] = useState<string>('');

  const [amount, setAmount] = useState<number>(0);

  const [duration, setDuration] = useState<number>(0);
  const [posteId, setPosteId] = useState<number | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [startContrat, setStartContrat] = useState<Date | undefined>(undefined);
  const [endContrat, setEndContrat] = useState<Date | undefined>(undefined);

  const [updateDevisMaintenance] = useMutation(GQL_UPDATE_DEVIS_MAINTENANCE, {
    client: authClient,
  });

  const [getAllUsers] = useLazyQuery(GQL_USERS, {
    client: authClient,
    onCompleted: (data) => {
      setUserList(data.getUsers);
    },
  });

  useEffect(() => {
    form.resetFields();

    void getUserList();
    setPosteId(maintenance.posteId);
    setAmount(maintenance.amount);
    setDuration(maintenance.duration);
    setTaskName(maintenance.task?.name ?? '');
    setStartContrat(maintenance.startDate as Date);
    setEndContrat(maintenance.finishDate as Date);

    if (maintenance.task?.users) {
      const _usersWithOutTimes = [...maintenance.task.users]
        .filter((el) => !el.hasTimes)
        .map((el) => el.id);

      setSelectedUsers([..._usersWithOutTimes]);

      const _usersWhithTimes = [...maintenance.task.users]
        .filter((el) => el.hasTimes)
        .map((u) => u.id);
      setUsersWithTimes(_usersWhithTimes);
    } else {
      setSelectedUsers([]);
      setUsersWithTimes([]);
    }
  }, [maintenance]);

  async function getUserList() {
    setLoading(true);
    await getAllUsers();
    setLoading(false);
  }

  async function submit() {
    if (!taskName || !posteId || !startContrat || !endContrat) return;
    const updateDevisMaintenanceInput = {
      id: maintenance.id,
      startDate: new Date(startContrat as Date).setHours(0, 0, 0),
      finishDate: new Date(endContrat as Date).setHours(23, 59, 59),
      amount: amount,
      duration: duration,
      taskName: taskName,
      posteId: posteId,
      users: selectedUsers.concat(usersWithTimes),
    };
    const { data: _data } = await updateDevisMaintenance({
      variables: {
        updateDevisMaintenanceInput,
      },
    });
    if (_data?.updateDevisMaintenanceAndTask) {
      confirm();
    } else {
      close();
    }
    setDuration(0);
    form.resetFields();
  }

  const onChangeAmount = (value: number | null) => {
    if (value !== null) setAmount(value);
  };

  const onChangeDuration = (_duration: number) => {
    setDuration(_duration);
  };

  function onChangePoste(value: number) {
    setPosteId(value);
    const _poste = postes.find((el) => el.id === value);
    if (!form.getFieldValue('taskName')) {
      const name = _poste?.name ?? '';
      form.setFieldsValue({ taskName: name });
      setTaskName(name);
    }
  }

  const onRangeChange = (values: RangeValue<Dayjs>) => {
    if (values) {
      setStartContrat(values[0]?.toDate());
      setEndContrat(values[1]?.toDate());
    }
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current: any) => {
    return current && current < dayjs().endOf('day');
  };

  return (
    <ModalCustom
      confirmDisabled={!taskName || !(duration || amount)}
      title={title}
      isOpen={isOpen}
      confirm={submit}
      close={() => {
        form.resetFields();
        close();
      }}
    >
      {loading ? (
        <p>{i18n._('common.loading')} ...</p>
      ) : (
        <>
          <Form
            form={form}
            {...layout}
            name="task-add"
            initialValues={{
              poste: maintenance.posteId,
              taskName: maintenance.task?.name ?? '',
              amount: maintenance.amount,
              range: [
                dayjs(maintenance.startDate as string),
                dayjs(maintenance.finishDate as string),
              ],
              duration: maintenance.duration,
              users: maintenance.task?.users
                ? [
                    ...maintenance.task.users
                      .filter((el) => !el.hasTimes)
                      .map((u) => u.id),
                  ]
                : [],
              usersWithTime: maintenance.task?.users
                ? [
                    ...maintenance.task.users
                      .filter((el) => el.hasTimes)
                      .map((u) => u.id),
                  ]
                : [],
            }}
            style={{ width: 600 }}
          >
            <Form.Item
              name="poste"
              label={i18n._('common.maintenance.poste')}
              rules={[
                {
                  required: true,
                  message: i18n._('form.maintenance.placeholder'),
                },
              ]}
            >
              <Select
                showSearch
                style={{ width: '100%' }}
                value={posteId}
                onChange={(value) => onChangePoste(value)}
                optionFilterProp="label"
                options={postes.map((item) => ({
                  value: item.id,
                  label: i18n._(item.name),
                }))}
              />
            </Form.Item>
            <Form.Item
              name="taskName"
              label={i18n._('common.maintenance.name')}
              rules={[
                {
                  required: true,
                  message: i18n._('form.maintenance.placeholder'),
                },
              ]}
            >
              <Input
                placeholder={i18n._('form.maintenance.placeholder')}
                onChange={(e) => setTaskName(e.target.value)}
                value={taskName}
              />
            </Form.Item>

            <Form.Item
              name="range"
              label={i18n._('form.rangeContrat')}
              rules={[{ required: true, message: i18n._('errors.required') }]}
            >
              <RangePicker
                style={{ width: '100%' }}
                disabledDate={disabledDate}
                onChange={onRangeChange}
              />
            </Form.Item>

            <Form.Item
              name="duration"
              style={{ width: '100%' }}
              label={i18n._('common.duration')}
              rules={[
                {
                  required: true,
                  message: i18n._('form.duration.placeholder'),
                },
              ]}
            >
              <DurationItem
                $allowCorrection
                duration={duration}
                onChange={onChangeDuration}
                $align="flex-start"
              />
            </Form.Item>

            <Form.Item
              name="amount"
              style={{ width: '100%' }}
              label={i18n._('common.amount')}
              rules={[
                {
                  required: true,
                  message: i18n._('form.amount.placeholder'),
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder={i18n._('form.amount.placeholder')}
                onChange={onChangeAmount}
                value={amount}
                controls={false}
                step="0.01"
                addonAfter="€"
              />
            </Form.Item>
            <Form.Item name="users" label={i18n._('form.edit.users')}>
              <Select
                mode="multiple"
                showSearch
                placeholder={i18n._('form.edit.users.placeholder')}
                value={selectedUsers}
                onChange={setSelectedUsers}
                style={{ width: '100%' }}
                optionFilterProp="label"
                options={getUsersListWithoutUsersWithTime(
                  userList,
                  usersWithTimes
                ).map((item) => ({
                  value: item.id,
                  label: item.firstName + ' ' + item.lastName,
                }))}
              />
            </Form.Item>
            <Divider />
            {usersWithTimes.length > 0 && (
              <>
                <span>{i18n._('users.edit.warning')}</span>
                <Select
                  mode="multiple"
                  showSearch
                  disabled
                  placeholder={i18n._('form.edit.users.placeholder')}
                  value={usersWithTimes}
                  style={{ width: '100%' }}
                  optionFilterProp="label"
                  options={userList.map((item) => ({
                    value: item.id,
                    label: item.firstName + ' ' + item.lastName,
                  }))}
                />
              </>
            )}
          </Form>
        </>
      )}
    </ModalCustom>
  );
}

export default EditMaintenance;
